import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, CalendarPlus, Link2, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MatterCombobox } from "@/components/MatterCombobox";
import { FeeNoteButton } from "@/components/FeeNoteButton";
import { api, errText } from "@/lib/api";
import { whenLabel } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

/** Tapping a listing asks what you want to do with it before anything opens. */
export const EntryActionsDialog = ({ event, onOpenChange, onEdit, onChanged }) => {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(event);
  const [matters, setMatters] = useState([]);
  const [linking, setLinking] = useState(false);
  const [nextDate, setNextDate] = useState("");
  const [askNext, setAskNext] = useState(false);

  useEffect(() => {
    setEntry(event);
    setAskNext(false);
    setNextDate("");
  }, [event]);

  useEffect(() => {
    if (!event || event.matter_id || matters.length) return;
    api
      .get("/matters")
      .then(({ data }) => setMatters(data))
      .catch(() => setMatters([]));
  }, [event, matters.length]);

  const link = async (matterId) => {
    if (!matterId) return;
    setLinking(true);
    try {
      const { data } = await api.patch(`/events/${entry.event_id}/matter`, {
        matter_id: matterId,
      });
      const matter = matters.find((m) => m.matter_id === matterId);
      setEntry({
        ...entry,
        ...(data?.event || {}),
        matter_id: matterId,
        matter_title: matter?.title,
        case_number: matter?.case_number,
      });
      toast.success(`Linked to ${matter?.title || "the matter"}`);
      onChanged?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLinking(false);
    }
  };

  const addNextDate = () => {
    if (!nextDate) return toast.error("Pick the next date first");
    const {
      event_id: _id,
      notes: _notes,
      created_at: _c,
      updated_at: _u,
      clash: _cl,
      clash_with: _cw,
      ...copy
    } = entry;
    onEdit?.({ ...copy, date: nextDate, notes: "" });
  };

  if (!entry) return null;

  return (
    <Dialog open={Boolean(event)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-sm p-5 gap-4 max-h-[75vh] overflow-y-auto" data-testid="entry-actions-dialog">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle className="font-heading text-lg leading-snug">{entry.title}</DialogTitle>
          <DialogDescription className="text-xs">
            {[
              whenLabel(entry),
              entry.court_name,
              entry.matter_title || "No matter linked",
              entry.case_number,
            ]
              .filter(Boolean)
              .join(" · ")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Button
            data-testid="entry-action-edit"
            onClick={() => onEdit?.(entry)}
            className="w-full rounded-full h-11 justify-start px-4 text-sm"
          >
            <Pencil className="h-4 w-4 mr-2.5" /> Edit diary entry
          </Button>

          {canWrite ? (
            <div data-testid="entry-action-next-date-panel">
              <Button
                variant="outline"
                data-testid="entry-action-next-date"
                onClick={() => setAskNext((v) => !v)}
                className="w-full rounded-full h-11 justify-start px-4 text-sm"
              >
                <CalendarPlus className="h-4 w-4 mr-2.5" /> Add next date
              </Button>
              {askNext ? (
                <div className="mt-2 space-y-2 border border-border rounded-sm p-3">
                  <Label className="label-caps">Copy this entry to</Label>
                  <Input
                    type="date"
                    data-testid="entry-next-date-input"
                    value={nextDate}
                    onChange={(e) => setNextDate(e.target.value)}
                    onClick={(e) => {
                      try {
                        e.currentTarget.showPicker?.();
                      } catch {
                        /* the browser opens it on its own */
                      }
                    }}
                    className="rounded-sm h-10 text-sm"
                  />
                  <Button
                    data-testid="entry-next-date-continue"
                    onClick={addNextDate}
                    className="w-full rounded-full h-10 text-sm"
                  >
                    Open the copy
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Everything carries over except the notes — adjust the time before you save.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {entry.matter_id ? (
            <>
              <Button
                variant="outline"
                data-testid="entry-action-matter"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/matters/${entry.matter_id}`);
                }}
                className="w-full rounded-full h-11 justify-start px-4 text-sm"
              >
                <Briefcase className="h-4 w-4 mr-2.5" /> Open matter page
              </Button>
              <FeeNoteButton
                matterId={entry.matter_id}
                eventId={entry.event_id}
                testId="entry-action-fee-note"
                className="w-full rounded-full h-11 justify-start px-4 text-sm [&>svg]:mr-2.5"
              />
            </>
          ) : (
            <div className="pt-1 space-y-2" data-testid="entry-action-link-panel">
              <Label className="label-caps inline-flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5" /> Link a matter
              </Label>
              <MatterCombobox
                matters={matters}
                value={null}
                onChange={link}
                testId="entry-action-link-matter"
                emptyLabel={linking ? "Linking…" : "Pick the matter"}
                height="h-11"
                disabled={!canWrite || linking}
              />
              <p className="text-xs text-muted-foreground">
                Link it and the matter page and fee notes open up right here.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
